<template>
  <!--
    Effectifs de la classe par âge et par genre.

    Un rapport d'ensemble, délibérément sans liste nominative : les fiches
    individuelles sont déjà dans le tableau de la classe, les redonner ici
    noierait la vue d'ensemble qu'on vient y chercher.

    Téléportée dans <body> : la carte qui porte le bouton la rognerait.
  -->
  <teleport to="body">
    <div class="abk-overlay" v-if="open" @click.self="close">
      <div class="abk-panel" role="dialog" aria-modal="true">
        <div class="abk-head">
          <div>
            <h6 class="mb-1">{{ $t("age_breakdown.title") }}</h6>
            <span class="abk-meta">
              {{ classroomLabel }}
              <span class="abk-dot">•</span>
              {{ $t("age_breakdown.students", { count: breakdown.totals.total }) }}
            </span>
          </div>
          <button type="button" class="abk-close" :aria-label="$t('button.close')" @click="close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <!-- Répartition de toute la classe, avant le détail par âge. -->
        <div class="abk-totals">
          <div class="abk-total">
            <span class="abk-total-label">{{ $t("age_breakdown.total") }}</span>
            <span class="abk-total-value">{{ breakdown.totals.total }}</span>
          </div>
          <div class="abk-total is-boys">
            <span class="abk-total-label">
              <i class="bi bi-gender-male me-1"></i>{{ $t("classdetails.boys") }}
            </span>
            <span class="abk-total-value">{{ breakdown.totals.boys }}</span>
          </div>
          <div class="abk-total is-girls">
            <span class="abk-total-label">
              <i class="bi bi-gender-female me-1"></i>{{ $t("classdetails.girls") }}
            </span>
            <span class="abk-total-value">{{ breakdown.totals.girls }}</span>
          </div>
        </div>

        <div class="abk-body">
          <p class="abk-empty" v-if="!breakdown.groups.length">
            {{ $t("age_breakdown.empty") }}
          </p>

          <template v-else>
            <table class="abk-table">
              <thead>
                <tr>
                  <th>{{ $t("age_breakdown.age") }}</th>
                  <th class="text-center">{{ $t("age_breakdown.count") }}</th>
                  <th class="text-center">{{ $t("classdetails.boys") }}</th>
                  <th class="text-center">{{ $t("classdetails.girls") }}</th>
                  <th class="abk-bar-col">{{ $t("age_breakdown.split") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="group in breakdown.groups" :key="group.age">
                  <td class="abk-age">{{ $t("age_breakdown.years", { count: group.age }) }}</td>
                  <td class="text-center abk-count">{{ group.total }}</td>
                  <td class="text-center abk-boys">{{ group.boys }}</td>
                  <td class="text-center abk-girls">{{ group.girls }}</td>
                  <td class="abk-bar-col">
                    <!--
                      La barre se lit plus vite que deux nombres à comparer.
                      Elle porte la part de la tranche dans la classe, pas
                      celle des genres entre eux : c'est l'effectif qu'on
                      cherche à situer.
                    -->
                    <div class="abk-bar" :title="$t('age_breakdown.share', {
                      percent: shareOf(group.total, breakdown.totals.total),
                    })">
                      <span
                        class="abk-bar-boys"
                        :style="{ width: barWidth(group.boys) }"
                      ></span>
                      <span
                        class="abk-bar-girls"
                        :style="{ width: barWidth(group.girls) }"
                      ></span>
                      <span
                        class="abk-bar-unknown"
                        :style="{ width: barWidth(group.unknownGender) }"
                      ></span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <!--
              Élèves sans date de naissance : leur âge est inconnu, ils ne
              peuvent figurer dans aucune tranche. Le dire évite que la somme
              des lignes paraisse fausse face à l'effectif.
            -->
            <p class="abk-note" v-if="breakdown.withoutDob">
              <i class="bi bi-info-circle me-1"></i>
              {{ $t("age_breakdown.without_dob", { count: breakdown.withoutDob }) }}
            </p>

            <p class="abk-note" v-if="breakdown.totals.unknownGender">
              <i class="bi bi-info-circle me-1"></i>
              {{
                $t("age_breakdown.without_gender", {
                  count: breakdown.totals.unknownGender,
                })
              }}
            </p>
          </template>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { buildAgeBreakdown, shareOf } from "@/utils/ageBreakdown";

export default {
  name: "age-breakdown-modal",
  props: {
    open: { type: Boolean, default: false },
    students: { type: Array, default: () => [] },
    classroom: { type: Object, default: () => ({}) },
  },
  emits: ["close"],
  computed: {
    breakdown() {
      return buildAgeBreakdown(this.students);
    },
    classroomLabel() {
      const room = this.classroom || {};
      return [room.grade_code, room.speciality_code, room.number]
        .filter(Boolean)
        .join(" ");
    },
    /** Effectif de la tranche la plus nombreuse, échelle des barres. */
    largestGroup() {
      return this.breakdown.groups.reduce(
        (max, group) => Math.max(max, group.total),
        0
      );
    },
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
    document.body.style.overflow = "";
  },
  watch: {
    open(value) {
      document.body.style.overflow = value ? "hidden" : "";
    },
  },
  methods: {
    shareOf,
    onKeydown(event) {
      if (event.key === "Escape" && this.open) this.close();
    },
    close() {
      this.$emit("close");
    },
    /**
     * Largeur d'un segment de barre.
     *
     * Rapportée à la tranche la plus nombreuse, et non à l'effectif total :
     * sur une classe de cinquante élèves répartis sur six âges, toutes les
     * barres seraient sinon des traits indistincts.
     */
    barWidth(count) {
      if (!this.largestGroup) return "0%";
      return `${(count / this.largestGroup) * 100}%`;
    },
  },
};
</script>

<style scoped>
.abk-overlay {
  position: fixed;
  inset: 0;
  /* Au-dessus de la barre latérale, qui monte à 9999. */
  z-index: 10050;
  background: rgba(8, 12, 24, 0.74);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.abk-panel {
  width: 100%;
  max-width: 780px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  background: var(--bs-card-bg, #141a2f);
  color: #e6ebf5;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.7);
  overflow: hidden;
}

.abk-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.abk-meta {
  font-size: 12px;
  opacity: 0.6;
}

.abk-dot {
  margin: 0 6px;
  opacity: 0.5;
}

.abk-close {
  background: transparent;
  border: none;
  color: inherit;
  opacity: 0.6;
  font-size: 14px;
  cursor: pointer;
  padding: 2px 4px;
  line-height: 1;
}

.abk-close:hover {
  opacity: 1;
}

.abk-totals {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.abk-total {
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
}

.abk-total.is-boys {
  border-color: rgba(94, 152, 228, 0.4);
  background: rgba(94, 152, 228, 0.1);
}

.abk-total.is-girls {
  border-color: rgba(228, 94, 160, 0.4);
  background: rgba(228, 94, 160, 0.1);
}

.abk-total-label {
  font-size: 11.5px;
  opacity: 0.7;
}

.abk-total-value {
  font-size: 22px;
  font-weight: 700;
}

.abk-body {
  flex: 1;
  overflow-y: auto;
  padding: 6px 20px 20px;
}

.abk-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}

.abk-table th,
.abk-table td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  white-space: nowrap;
}

.abk-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--bs-card-bg, #141a2f);
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.85;
}

.abk-age {
  font-weight: 600;
}

.abk-count {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.abk-boys {
  color: #8ab6ee;
  font-variant-numeric: tabular-nums;
}

.abk-girls {
  color: #e98cbd;
  font-variant-numeric: tabular-nums;
}

.abk-bar-col {
  width: 38%;
}

.abk-bar {
  display: flex;
  height: 9px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.07);
}

.abk-bar-boys {
  background: #5e98e4;
}

.abk-bar-girls {
  background: #e45ea0;
}

.abk-bar-unknown {
  background: rgba(255, 255, 255, 0.28);
}

.abk-note {
  margin: 12px 0 0;
  font-size: 11.5px;
  line-height: 1.45;
  opacity: 0.6;
}

.abk-empty {
  margin: 0;
  padding: 48px 20px;
  text-align: center;
  font-size: 13px;
  opacity: 0.6;
}

@media (max-width: 575px) {
  .abk-overlay {
    padding: 12px;
  }

  /* Trop étroit pour une barre lisible : les nombres suffisent. */
  .abk-bar-col {
    display: none;
  }
}
</style>
